import { ReactNode } from 'react';

export interface Column<T> {
  key: string;
  header: string;
  render?: (item: T, index: number) => ReactNode;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (item: T, index: number) => string;
  emptyMessage?: string;
  isLoading?: boolean;
  className?: string;
  onRowClick?: (item: T, index: number) => void;
}

export const Table = <T,>({
  columns,
  data,
  keyExtractor,
  emptyMessage = 'No data available',
  isLoading = false,
  className = '',
  onRowClick,
}: TableProps<T>) => {
  const getAlignClass = (align?: 'left' | 'center' | 'right') => {
    switch (align) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      default:
        return 'text-left';
    }
  };

  if (isLoading) {
    return (
      <div className={`w-full overflow-x-auto ${className}`}>
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[#32323B]">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-4 py-3 text-sm font-medium text-white/60 ${getAlignClass(
                    column.align,
                  )}`}
                  style={{ width: column.width }}>
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...Array(5)].map((_, index) => (
              <tr
                key={`loading-${index}`}
                className="border-b border-[#32323B]">
                {columns.map((column) => (
                  <td key={column.key} className="px-4 py-4">
                    <div className="h-4 bg-gray-700 rounded animate-pulse"></div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={`w-full overflow-x-auto ${className}`}>
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[#32323B]">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-4 py-3 text-sm font-medium text-white/60 ${getAlignClass(
                    column.align,
                  )}`}
                  style={{ width: column.width }}>
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
        </table>
        <div className="text-center py-8 text-white/60">{emptyMessage}</div>
      </div>
    );
  }

  return (
    <div className={`w-full overflow-x-auto ${className}`}>
      <table className="w-full border-collapse table-fixed">
        <thead>
          <tr className="border-b border-[#32323B]">
            {columns.map((column) => (
              <th
                key={column.key}
                className={`pr-4 py-3 text-sm font-medium text-white/60 ${getAlignClass(
                  column.align,
                )}`}
                style={{ width: column.width }}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr
              key={keyExtractor(item, index)}
              className={`border-b border-[#32323B] transition-colors ${
                onRowClick ? 'cursor-pointer hover:bg-white/5' : ''
              }`}
              onClick={() => onRowClick?.(item, index)}>
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={`pr-4 py-4 text-sm text-white ${getAlignClass(
                    column.align,
                  )}`}
                  style={{ maxWidth: 0 }}>
                  {column.render
                    ? column.render(item, index)
                    : String((item as any)[column.key] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
